import { useState, useMemo, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@helios/ui/button';
import {
  qPatientContact,
  qPatientCommunicationPreferences,
  qNotificationJobsByPatient,
  qAppointmentWhatsappResponsesByAppointmentIds,
  type PatientContactRow,
  type PatientCommunicationPreferencesClientRow,
  type NotificationJobClientRow,
  type AppointmentWhatsAppResponseClientRow,
} from '@helios/data-access';
import { NavIcon, useBrandTokens } from '../../shell/shared';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { SendWhatsAppReminderAction } from '../../components/SendWhatsAppReminderAction';
import {
  AppointmentResponseBadge,
  indexLatestResponseByAppointmentId,
} from '../../components/AppointmentResponseBadge';
import { appointmentReminderReference } from '../../lib/appointment-reminder';
import {
  usePatientAppointmentsData,
  appointmentToPrefill,
  type EnrichedAppointment,
} from '../appointments/useAppointmentsData';
import { useAppointmentCalendarSettings } from '../appointments/useAppointmentCalendarSettings';
import {
  BookAppointmentDrawer,
  type BookDrawerPrefill,
} from '../appointments/BookAppointmentDrawer';

// ─── Constants ───

const COLUMNS = [
  'DATE',
  'TIME',
  'SERVICE / CATEGORY',
  'PROVIDER',
  'STATUS',
  'NOTES',
  'ACTIONS',
] as const;

// ─── Helpers ───

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

function computeEndTime(startTime: string, durationMinutes: number): string {
  const [h, m] = startTime.split(':').map(Number);
  const totalMin = h * 60 + (m || 0) + durationMinutes;
  const endH = Math.floor(totalMin / 60) % 24;
  const endM = totalMin % 60;
  return `${pad2(endH)}:${pad2(endM)}`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[Number(m) - 1]} ${Number(d)}, ${y}`;
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// ─── Patient Appointments Page ───

export function PatientAppointmentsPage() {
  const { id: patientId } = useParams<{ id: string }>();
  const calSettings = useAppointmentCalendarSettings();
  const brandTokens = useBrandTokens();

  const { appointments, lookups } = usePatientAppointmentsData(
    patientId ?? '',
    calSettings.colorAppointmentsBy,
  );

  // PRD.221 — patient-level data for the "Send WhatsApp Reminder" action.
  // Queried once here (not per-row) since every row shares the same patient.
  const { data: contactRows } = usePowerSyncQuery<PatientContactRow>(
    qPatientContact(patientId ?? ''),
  );
  const { data: commPrefRows } = usePowerSyncQuery<PatientCommunicationPreferencesClientRow>(
    qPatientCommunicationPreferences(patientId ?? ''),
  );
  const { data: reminderJobs } = usePowerSyncQuery<NotificationJobClientRow>(
    qNotificationJobsByPatient(patientId ?? ''),
  );

  const patientPhone = contactRows[0]?.mobile_number ?? null;
  // Require an explicit, non-revoked opt-in — a missing prefs row = not opted in.
  const whatsAppOptedIn =
    commPrefRows[0]?.whatsapp_opt_in === 1 && !commPrefRows[0]?.whatsapp_opt_out_at;

  // PRD.224 — pull all WhatsApp responses for the rendered appointment ids
  // in a single watch, then index them per-row. Keeps the query stable until
  // the appointment set changes.
  const appointmentIds = useMemo(() => appointments.map((a) => a.id), [appointments]);
  const { data: responseRows } = usePowerSyncQuery<AppointmentWhatsAppResponseClientRow>(
    qAppointmentWhatsappResponsesByAppointmentIds(appointmentIds),
  );
  const latestResponseByAppointment = useMemo(
    () => indexLatestResponseByAppointmentId(responseRows),
    [responseRows],
  );

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [bookDrawerOpen, setBookDrawerOpen] = useState(false);
  const [drawerPrefill, setDrawerPrefill] = useState<BookDrawerPrefill>({
    patientId: patientId ?? '',
  });

  const filtered = useMemo(() => {
    return appointments.filter((a) => {
      if (search) {
        const q = search.toLowerCase();
        if (
          !a.serviceName.toLowerCase().includes(q) &&
          !a.providerName.toLowerCase().includes(q) &&
          !a.notes.toLowerCase().includes(q)
        )
          return false;
      }
      if (statusFilter && a.statusId !== statusFilter) return false;
      if (providerFilter && a.providerId !== providerFilter) return false;
      if (categoryFilter && a.categoryId !== categoryFilter) return false;
      return true;
    });
  }, [appointments, search, statusFilter, providerFilter, categoryFilter]);

  const todayLabel = formatDate(todayStr());

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--sp-4)',
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-xl)',
              color: 'var(--text)',
              margin: 0,
            }}
          >
            Appointments
          </h2>
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              margin: '2px 0 0',
            }}
          >
            {todayLabel}
          </p>
        </div>
        <Button
          variant="gradient"
          size="md"
          onClick={() => {
            setDrawerPrefill({ patientId: patientId ?? '' });
            setBookDrawerOpen(true);
          }}
        >
          + Book Appointment
        </Button>
      </div>

      {/* Filters Bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          marginBottom: 'var(--sp-4)',
          flexWrap: 'wrap',
        }}
      >
        {/* Search */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            maxWidth: 320,
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 12,
              color: 'var(--text-muted)',
              display: 'flex',
              pointerEvents: 'none',
            }}
          >
            <NavIcon name="search" />
          </div>
          <input
            type="text"
            placeholder="Search service, provider, notes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              height: 'var(--input-height)',
              padding: '0 var(--input-padding-x) 0 40px',
              borderRadius: 'var(--input-radius)',
              border: '1px solid var(--border-input)',
              background: 'var(--input-bg)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--input-text)',
              outline: 'none',
            }}
          />
        </div>

        {/* Status filter */}
        <select
          style={selectStyle}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">Status</option>
          {lookups.statusList.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>

        {/* Provider filter */}
        <select
          style={selectStyle}
          value={providerFilter}
          onChange={(e) => setProviderFilter(e.target.value)}
        >
          <option value="">Provider</option>
          {lookups.providerList.map((p) => (
            <option key={p.id} value={p.id}>
              {p.display_name || p.full_name}
            </option>
          ))}
        </select>

        {/* Category filter */}
        <select
          style={selectStyle}
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="">Category</option>
          {lookups.categoryList.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
        }}
      >
        {filtered.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'var(--sp-12)',
              color: 'var(--text-muted)',
              gap: 'var(--sp-3)',
            }}
          >
            <NavIcon name="calendar" />
            <span
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semi)',
              }}
            >
              No appointments found
            </span>
            <span style={{ fontSize: 'var(--font-size-sm)' }}>
              {appointments.length === 0
                ? 'This patient has no appointments yet'
                : 'Try adjusting your filters or search query'}
            </span>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <th
                    key={col}
                    style={{
                      padding: 'var(--sp-3) var(--sp-4)',
                      textAlign: 'left',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-semi)',
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '1px solid var(--border)',
                      background: 'var(--bg-surface)',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((appt) => (
                <AppointmentRow
                  key={appt.id}
                  appt={appt}
                  openMenu={openMenu}
                  setOpenMenu={setOpenMenu}
                  onEdit={() => {
                    setDrawerPrefill(appointmentToPrefill(appt));
                    setBookDrawerOpen(true);
                  }}
                  patientPhone={patientPhone}
                  whatsAppOptedIn={whatsAppOptedIn}
                  reminderJobs={reminderJobs}
                  whatsAppResponse={latestResponseByAppointment.get(appt.id) ?? null}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Book Appointment Drawer — patient locked to this patient */}
      <BookAppointmentDrawer
        open={bookDrawerOpen}
        onOpenChange={setBookDrawerOpen}
        brandTokens={brandTokens}
        prefill={drawerPrefill}
      />
    </div>
  );
}

// ─── Appointment Row ───

function AppointmentRow({
  appt,
  openMenu,
  setOpenMenu,
  onEdit,
  patientPhone,
  whatsAppOptedIn,
  reminderJobs,
  whatsAppResponse,
}: {
  appt: EnrichedAppointment;
  openMenu: string | null;
  setOpenMenu: (id: string | null) => void;
  onEdit: () => void;
  patientPhone: string | null;
  whatsAppOptedIn: boolean;
  reminderJobs: NotificationJobClientRow[];
  whatsAppResponse: AppointmentWhatsAppResponseClientRow | null;
}) {
  const endTime = computeEndTime(appt.startTime, appt.durationMinutes);

  // Latest reminder job for this specific appointment (jobs are ordered
  // scheduled_at DESC, so the first match is the most recent).
  const reminderRef = appointmentReminderReference(appt.id);
  const reminderJob = reminderJobs.find((j) => j.client_reference === reminderRef) ?? null;

  return (
    <tr style={{ borderBottom: '1px solid var(--border)' }}>
      {/* Date */}
      <td style={cellStyle}>
        <div style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}>
          {formatDate(appt.date)}
        </div>
      </td>

      {/* Time */}
      <td style={cellStyle}>
        <div style={{ fontWeight: 'var(--font-weight-semi)', color: 'var(--text)' }}>
          {appt.startTime}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>{endTime}</div>
      </td>

      {/* Service / Category */}
      <td style={cellStyle}>
        <div style={{ color: 'var(--text)' }}>{appt.serviceName || '—'}</div>
        {appt.categoryName && (
          <span
            style={{
              display: 'inline-block',
              marginTop: 2,
              padding: '1px 8px',
              borderRadius: 'var(--radius-full)',
              background: appt.color,
              color: '#475569',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
              lineHeight: '18px',
            }}
          >
            {appt.categoryName}
          </span>
        )}
      </td>

      {/* Provider */}
      <td style={cellStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: 'var(--radius-full)',
              background: appt.accentColor || '#dbeafe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 9,
              fontWeight: 700,
              color: '#fff',
              flexShrink: 0,
            }}
          >
            {getInitials(appt.providerName)}
          </div>
          <span style={{ color: 'var(--text)' }}>{appt.providerName}</span>
        </div>
      </td>

      {/* Status */}
      <td style={cellStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
          {appt.statusLabel && (
            <span
              style={{
                display: 'inline-block',
                padding: '2px 10px',
                borderRadius: 'var(--radius-full)',
                background: '#f1f5f9',
                color: '#475569',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-medium)',
                lineHeight: '20px',
                whiteSpace: 'nowrap',
              }}
            >
              {appt.statusLabel}
            </span>
          )}
          <AppointmentResponseBadge response={whatsAppResponse} />
        </div>
      </td>

      {/* Notes */}
      <td
        style={{
          ...cellStyle,
          color: appt.notes ? 'var(--text)' : 'var(--text-muted)',
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {appt.notes || '—'}
      </td>

      {/* Actions */}
      <td style={{ ...cellStyle, position: 'relative' }}>
        <button
          type="button"
          onClick={() => setOpenMenu(openMenu === appt.id ? null : appt.id)}
          style={{
            width: 28,
            height: 28,
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: openMenu === appt.id ? 'var(--bg-surface-2)' : 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-muted)',
            padding: 0,
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="5" r="1" />
            <circle cx="12" cy="12" r="1" />
            <circle cx="12" cy="19" r="1" />
          </svg>
        </button>

        {openMenu === appt.id && (
          <>
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 9 }}
              onClick={() => setOpenMenu(null)}
            />
            <div
              style={{
                position: 'absolute',
                right: 'var(--sp-4)',
                top: '100%',
                marginTop: -4,
                width: 240,
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--shadow-lg)',
                zIndex: 10,
                overflow: 'hidden',
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setOpenMenu(null);
                  onEdit();
                }}
                style={menuActionStyle('var(--text)')}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface-2)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'none';
                }}
              >
                Edit
              </button>

              <SendWhatsAppReminderAction
                appointment={{
                  id: appt.id,
                  patientId: appt.patientId,
                  patientName: appt.patientName,
                  date: appt.date,
                  startTime: appt.startTime,
                }}
                patientPhone={patientPhone}
                whatsAppOptedIn={whatsAppOptedIn}
                reminderJob={reminderJob}
                onConfirmOpen={() => setOpenMenu(null)}
              />

              <button
                type="button"
                onClick={() => setOpenMenu(null)}
                style={menuActionStyle('#dc2626')}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface-2)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'none';
                }}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </td>
    </tr>
  );
}

// ─── Shared Styles ───

const cellStyle: CSSProperties = {
  padding: 'var(--sp-3) var(--sp-4)',
  fontSize: 'var(--font-size-sm)',
  verticalAlign: 'middle',
};

function menuActionStyle(color: string): CSSProperties {
  return {
    width: '100%',
    padding: 'var(--sp-2) var(--sp-3)',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    fontSize: 'var(--font-size-sm)',
    color,
    cursor: 'pointer',
  };
}

const selectStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--sp-8) 0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--input-text)',
  outline: 'none',
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
};
